
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {TimezoneDeleteResponse} from './TimezoneDeleteResponse';

/**
 * TimezoneDeleteRequest struct
 */
class TimezoneDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} timezoneId
   * @param {string=} timezoneName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), timezoneId = null, timezoneName = null) {
    super()
    super.copy(parent)
    this.TimezoneId = timezoneId
    this.TimezoneName = timezoneName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TimezoneDeleteRequest}
   * @param {!TimezoneDeleteRequest} other Other struct
   * @returns {!TimezoneDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.TimezoneId != null) {
      this.TimezoneId = new UUID(other.TimezoneId)
    } else {
      this.TimezoneId = null
    }
    if (other.TimezoneName != null) {
      this.TimezoneName = other.TimezoneName
    } else {
      this.TimezoneName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TimezoneDeleteRequest}
   * @returns {!TimezoneDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TimezoneDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TimezoneDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TimezoneDeleteRequest}
   * @param {!TimezoneDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TimezoneDeleteRequest)) {
      throw new TypeError('Instance of TimezoneDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TimezoneDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      TimezoneId: ((this.TimezoneId != null) ? this.TimezoneId.toString() : null),
      TimezoneName: ((this.TimezoneName != null) ? this.TimezoneName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TimezoneDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TimezoneDeleteRequest} other Object value
   * @returns {!TimezoneDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new TimezoneDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TimezoneDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TimezoneDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TimezoneDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6071
  }
}

export { TimezoneDeleteRequest };

/**
 * Fast Binary Encoding TimezoneDeleteRequest field model
 */
class FieldModelTimezoneDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.TimezoneId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.TimezoneName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.TimezoneId.fbeOffset + this.TimezoneId.fbeSize), buffer, this.TimezoneId.fbeOffset + this.TimezoneId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.TimezoneId.fbeSize + this.TimezoneName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.TimezoneId.fbeExtra + this.TimezoneName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTimezoneDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6071
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.TimezoneId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimezoneId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimezoneId.fbeSize

    if ((fbeCurrentSize + this.TimezoneName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TimezoneName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimezoneName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!TimezoneDeleteRequest} fbeValue Default value, defaults is new TimezoneDeleteRequest()
   * @returns {!TimezoneDeleteRequest} TimezoneDeleteRequest value
   */
  get (fbeValue = new TimezoneDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!TimezoneDeleteRequest} fbeValue TimezoneDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.TimezoneId.fbeSize) <= fbeStructSize) {
      fbeValue.TimezoneId = this.TimezoneId.get()
    } else {
      fbeValue.TimezoneId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimezoneId.fbeSize

    if ((fbeCurrentSize + this.TimezoneName.fbeSize) <= fbeStructSize) {
      fbeValue.TimezoneName = this.TimezoneName.get()
    } else {
      fbeValue.TimezoneName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TimezoneName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTimezoneDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {!TimezoneDeleteRequest} fbeValue TimezoneDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTimezoneDeleteRequest}
   * @param {TimezoneDeleteRequest} fbeValue TimezoneDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.TimezoneId.set(fbeValue.TimezoneId)
    this.TimezoneName.set(fbeValue.TimezoneName)
  }
}

export { FieldModelTimezoneDeleteRequest };

/**
 * Fast Binary Encoding TimezoneDeleteRequest model
 */
class TimezoneDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTimezoneDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the TimezoneDeleteRequest model
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!FieldModelTimezoneDeleteRequest} model TimezoneDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TimezoneDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTimezoneDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TimezoneDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TimezoneDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TimezoneDeleteRequestModel}
   * @param {!TimezoneDeleteRequest} value TimezoneDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TimezoneDeleteRequestModel}
   * @param {!TimezoneDeleteRequest} value TimezoneDeleteRequest value, defaults is new TimezoneDeleteRequest()
   * @return {!object} Deserialized TimezoneDeleteRequest value and its size
   */
  deserialize (value = new TimezoneDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TimezoneDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TimezoneDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TimezoneDeleteRequestModel}
   * @param {!number} prev Previous TimezoneDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TimezoneDeleteRequestModel };
TimezoneDeleteRequest.__has_response = true;
TimezoneDeleteRequest.__response_class = TimezoneDeleteResponse;
